import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, ConsolePage, DataTable, Icon, Modal, Panel, QueryState, ListSkeleton, Td, type Column } from '@/components';
import { useToast } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { useUsers, useUpdateUserRole, useSetUserStatus, useAdminUpdateProfile, useOrg } from '@/hooks/queries';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { roleDefs } from '@/data/roleDefs';
import { professions, PROFESSION_IDS } from '@/data/professions';
import { assignableRoles } from '@/lib/rbac';
import { fullName } from '@/lib/format';
import { useLocale } from '@/i18n/LocaleProvider';
import type { Profile, Profession, Role } from '@/types/domain';

const ROLE_ORDER: Role[] = ['staff', 'instructor', 'admin', 'superadmin'];

function RoleBadge({ role, dot = true }: { role: Role; dot?: boolean }) {
  const r = roleDefs[role];
  return <Badge kind={r.tone as never} dot={dot}>{r.label.en}</Badge>;
}

const COLS: Column[] = [
  { label: 'User' },
  { label: 'Department' },
  { label: 'Role' },
  { label: 'Status' },
  { label: 'Last active' },
  { label: '' },
];

export function AdminUsers() {
  const { toast } = useToast();
  const { user: actor } = useAuth();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { locale } = useLocale();
  const usersQuery = useUsers();
  const users = usersQuery.data ?? [];
  const updateRole = useUpdateUserRole();
  const setStatus = useSetUserStatus();
  const [filter, setFilter] = useState<Role | 'all'>('all');
  const [selId, setSelId] = useState<string | null>(null);
  const [confirmSuspend, setConfirmSuspend] = useState<Profile | null>(null);
  const [editing, setEditing] = useState(false);
  const [deptFilter, setDeptFilter] = useState('all');
  const [unitFilter, setUnitFilter] = useState('all');
  const [search, setSearch] = useState('');

  const counts = ROLE_ORDER.reduce<Record<string, number>>((m, r) => ((m[r] = users.filter((u) => u.role === r).length), m), {});
  const deptOptions = [...new Set(users.map((u) => u.deptId).filter(Boolean))].sort();
  const unitOptions = [...new Set(users.filter((u) => deptFilter === 'all' || u.deptId === deptFilter).map((u) => u.unitId).filter(Boolean))].sort();
  const q = search.trim().toLowerCase();
  const list = users.filter(
    (u) =>
      (filter === 'all' || u.role === filter) &&
      (deptFilter === 'all' || u.deptId === deptFilter) &&
      (unitFilter === 'all' || u.unitId === unitFilter) &&
      (q === '' || `${u.firstEn} ${u.lastEn} ${u.firstTh} ${u.lastTh} ${u.email ?? ''}`.toLowerCase().includes(q)),
  );
  const sel = users.find((u) => u.id === selId) ?? list[0];
  const canAssign = assignableRoles(actor?.role);
  // You cannot change your OWN role — prevents the self-demotion lockout.
  // (The database also enforces this; the owner is permanently superadmin.)
  const isOwnRow = !!actor && !!sel && sel.id === actor.id;

  return (
    <ConsolePage title="User management" th="จัดการผู้ใช้งาน" crumbs="PEOPLE" maxWidth={1180} actions={<button className="btn btn-primary btn-sm" onClick={() => navigate('/admin/invites')}><Icon name="plus" size={15} /> Invite user</button>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }} className="max-md:!grid-cols-2">
          {ROLE_ORDER.map((r) => {
            const on = filter === r;
            const tone = roleDefs[r].tone;
            return (
              <button key={r} onClick={() => setFilter(on ? 'all' : r)} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>
                <div className="card" style={{ padding: 16, borderColor: on ? `var(--${tone === 'gray' ? 'line-2' : tone})` : 'var(--line)', borderWidth: on ? 1.5 : 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <RoleBadge role={r} dot={false} />
                    <span style={{ fontSize: 22, fontWeight: 600 }}>{counts[r]}</span>
                  </div>
                  <div className="th muted" style={{ fontSize: 11.5 }}>{roleDefs[r].label.th}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="field" style={{ flex: '2 1 220px', minWidth: 0, marginBottom: 0 }}>
            <span className="label">Search</span>
            <input className="input" placeholder="Name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <label className="field" style={{ flex: '1 1 160px', minWidth: 0, marginBottom: 0 }}>
            <span className="label">Department</span>
            <select className="select" value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setUnitFilter('all'); }}>
              <option value="all">All departments</option>
              {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: '1 1 160px', minWidth: 0, marginBottom: 0 }}>
            <span className="label">Unit</span>
            <select className="select" value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
              <option value="all">All units</option>
              {unitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: 18, alignItems: 'start' }} className="max-md:!grid-cols-1">
          <Panel title={`Users · ${list.length}`} pad={false}>
            <QueryState query={usersQuery} skeleton={<ListSkeleton rows={6} />} isEmpty={() => list.length === 0} emptyIcon="users" emptyTitle="No users found">
              {() => isDesktop ? (
                <DataTable
                  cols={COLS}
                  rows={list}
                  renderRow={(u) => (
                    <>
                      <Td>
                        <button onClick={() => setSelId(u.id)} style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <Avatar initials={u.initials} size={32} tone={u.tone} photoUrl={u.photoUrl} />
                            <div>
                              <div style={{ fontWeight: 500 }}>{fullName(u, locale)}</div>
                              <div className="th muted" style={{ fontSize: 12 }}>{u.firstTh} {u.lastTh}</div>
                            </div>
                          </div>
                        </button>
                      </Td>
                      <Td><span style={{ fontSize: 13 }}>{u.deptId}</span><div className="muted" style={{ fontSize: 11.5 }}>{u.unitId}</div></Td>
                      <Td><RoleBadge role={u.role} /></Td>
                      <Td>{u.status === 'active' ? <Badge kind="safe" dot>Active</Badge> : <Badge kind="danger" dot>Suspended</Badge>}</Td>
                      <Td><span className="muted" style={{ fontSize: 12.5 }}>{u.lastActive}</span></Td>
                      <Td align="center"><button className="btn-quiet btn-sm" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} onClick={() => setSelId(u.id)}><Icon name="dots" size={18} /></button></Td>
                    </>
                  )}
                />
              ) : (
                // Mobile: a tappable card list instead of the wide table (no overflow).
                <div className="divide">
                  {list.map((u) => (
                    <button key={u.id} onClick={() => setSelId(u.id)} style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px' }}>
                        <Avatar initials={u.initials} size={38} tone={u.tone} photoUrl={u.photoUrl} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 500, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fullName(u, locale)}</div>
                          <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                            <RoleBadge role={u.role} dot={false} />
                            {u.status === 'active' ? <Badge kind="safe" dot>Active</Badge> : <Badge kind="danger" dot>Suspended</Badge>}
                            <span className="muted" style={{ fontSize: 11.5 }}>{u.deptId}{u.unitId ? ` · ${u.unitId}` : ''}</span>
                          </div>
                        </div>
                        <Icon name="chevR" size={16} style={{ color: 'var(--ink-4)', flex: 'none' }} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </QueryState>
          </Panel>

          {sel && (
            <Panel title="Manage access">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                <Avatar initials={sel.initials} size={60} tone={sel.tone} photoUrl={sel.photoUrl} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{fullName(sel, locale)}</div>
                  <div className="th muted" style={{ fontSize: 12.5 }}>{sel.firstTh} {sel.lastTh} · {sel.deptId}</div>
                </div>
                {sel.status === 'active' ? <Badge kind="safe" dot>Active</Badge> : <Badge kind="danger" dot>Suspended</Badge>}
              </div>

              <div className="card divide" style={{ marginBottom: 16, background: 'var(--card-2)' }}>
                {(([
                  ['Email', sel.email || '—'],
                  ['Profession', sel.profession || '—'],
                  ['Unit', `${sel.deptId || '—'} · ${sel.unitId || '—'}`],
                  ['Last active', sel.lastActive || '—'],
                ]) as [string, string][]).map(([l, v], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 14px', fontSize: 13 }}>
                    <span className="muted" style={{ flex: 'none' }}>{l}</span>
                    <span style={{ fontWeight: 500, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>{v}</span>
                  </div>
                ))}
              </div>

              <button className="btn btn-ghost btn-block" style={{ marginBottom: 16 }} onClick={() => setEditing(true)}>
                <Icon name="pencil" size={15} /> Edit name & details
              </button>

              <div className="field" style={{ marginBottom: 16 }}>
                <span className="label">Assigned role</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {ROLE_ORDER.map((r) => {
                    const on = sel.role === r;
                    const allowed = canAssign.includes(r) && !isOwnRow;
                    return (
                      <button key={r} disabled={!allowed} onClick={() => { if (allowed) updateRole.mutate({ id: sel.id, role: r }); }} style={{ all: 'unset', cursor: allowed ? 'pointer' : 'not-allowed', display: 'block', opacity: allowed ? 1 : 0.5 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 9, border: `1.5px solid ${on ? 'var(--sage)' : 'var(--line)'}`, background: on ? 'var(--sage-tint)' : 'transparent' }}>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <div style={{ width: 16, height: 16, borderRadius: '50%', flex: 'none', border: `1.6px solid ${on ? 'var(--sage)' : 'var(--line-2)'}`, background: on ? 'var(--sage)' : 'transparent' }} />
                            <div>
                              <div style={{ fontSize: 13.5, fontWeight: 500 }}>{roleDefs[r].label.en}</div>
                              <div className="th muted" style={{ fontSize: 11 }}>{roleDefs[r].label.th}</div>
                            </div>
                          </div>
                          {r === 'superadmin' && <Icon name="shield" size={15} style={{ color: 'var(--spark)' }} />}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {isOwnRow && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, padding: '10px 12px', background: 'var(--spark-tint)', borderRadius: 9, fontSize: 12, color: 'var(--spark-deep)' }}>
                    <Icon name="shield" size={14} style={{ flex: 'none', marginTop: 1 }} /> You can't change your own role. Ask another Super Admin if it ever needs to change.
                  </div>
                )}
              </div>

              <div className="card" style={{ padding: 14, background: 'var(--card-2)', marginBottom: 16 }}>
                <div className="eyebrow" style={{ marginBottom: 8 }}>Permissions · {roleDefs[sel.role].label.en}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {roleDefs[sel.role].perms.map((p, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--ink-2)' }}>
                      <Icon name="check" size={13} style={{ color: 'var(--sage)' }} /> {p}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  className="btn btn-ghost"
                  style={{ flex: 1, color: sel.status === 'active' ? 'var(--danger)' : 'var(--safe-deep)' }}
                  onClick={() => {
                    if (sel.status === 'active') { setConfirmSuspend(sel); return; }
                    setStatus.mutate({ id: sel.id, status: 'active' });
                    toast(`${fullName(sel, locale)} reactivated`, { tone: 'sage', icon: 'unlock' });
                  }}
                >
                  {sel.status === 'active' ? <><Icon name="lock" size={15} /> Suspend</> : <><Icon name="unlock" size={15} /> Reactivate</>}
                </button>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => toast('Access updated', { icon: 'check' })}><Icon name="check" size={15} /> Save</button>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14, padding: '10px 12px', background: 'var(--spark-tint)', borderRadius: 9, fontSize: 12, color: 'var(--spark-deep)' }}>
                <Icon name="shield" size={14} style={{ flex: 'none', marginTop: 1 }} /> Only a Super Admin can assign Admin or Super Admin roles.
              </div>
            </Panel>
          )}
        </div>
      </div>

      <Modal open={!!confirmSuspend} onClose={() => setConfirmSuspend(null)} title="Suspend user?" width={380}>
        {confirmSuspend && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55 }}>
              Suspend <strong>{fullName(confirmSuspend, locale)}</strong>? They'll be signed out and blocked from signing in until you reactivate them.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setConfirmSuspend(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)' }}
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate({ id: confirmSuspend.id, status: 'suspended' }, { onSuccess: () => { toast(`${fullName(confirmSuspend, locale)} suspended`, { tone: 'clay', icon: 'lock' }); setConfirmSuspend(null); } })}
              >
                Suspend
              </button>
            </div>
          </div>
        )}
      </Modal>

      {editing && sel && <EditDetailsModal user={sel} onClose={() => setEditing(false)} />}
    </ConsolePage>
  );
}

/** Super-admin edit of a user's identity (name, profession, title, unit, email).
 *  Role + status are managed separately in the panel. Writes via adminUpdateProfile. */
function EditDetailsModal({ user, onClose }: { user: Profile; onClose: () => void }) {
  const { toast } = useToast();
  const adminUpdate = useAdminUpdateProfile();
  const { data: org = [] } = useOrg();
  const [firstEn, setFirstEn] = useState(user.firstEn);
  const [lastEn, setLastEn] = useState(user.lastEn);
  const [firstTh, setFirstTh] = useState(user.firstTh);
  const [lastTh, setLastTh] = useState(user.lastTh);
  const [profession, setProfession] = useState<Profession>(user.profession ?? 'NURSE');
  const [titlePrefix, setTitlePrefix] = useState(user.titlePrefix ?? '');
  const [titleSuffix, setTitleSuffix] = useState(user.titleSuffix ?? '');
  const [deptId, setDeptId] = useState(user.deptId);
  const [unitId, setUnitId] = useState(user.unitId);
  const [email, setEmail] = useState(user.email);

  const prof = professions[profession];
  const units = org.find((d) => d.deptId === deptId)?.units ?? [];

  const save = () =>
    adminUpdate.mutate(
      { id: user.id, patch: { firstEn, lastEn, firstTh, lastTh, profession, titlePrefix, titleSuffix, deptId, unitId, email } },
      {
        onSuccess: () => { toast('Profile updated', { icon: 'check' }); onClose(); },
        onError: () => toast('Could not save changes', { tone: 'clay', icon: 'x' }),
      },
    );

  return (
    <Modal open onClose={onClose} title="Edit name & details" width={480}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <label className="field" style={{ flex: 1 }}><span className="label">First name (EN)</span><input className="input" value={firstEn} onChange={(e) => setFirstEn(e.target.value)} /></label>
          <label className="field" style={{ flex: 1 }}><span className="label">Last name (EN)</span><input className="input" value={lastEn} onChange={(e) => setLastEn(e.target.value)} /></label>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <label className="field" style={{ flex: 1 }}><span className="label">ชื่อ (TH)</span><input className="input" value={firstTh} onChange={(e) => setFirstTh(e.target.value)} /></label>
          <label className="field" style={{ flex: 1 }}><span className="label">นามสกุล (TH)</span><input className="input" value={lastTh} onChange={(e) => setLastTh(e.target.value)} /></label>
        </div>
        <label className="field">
          <span className="label">Profession · วิชาชีพ</span>
          <select className="select" value={profession} onChange={(e) => { const p = e.target.value as Profession; setProfession(p); setTitlePrefix(professions[p].prefixes[0]?.th ?? ''); setTitleSuffix(''); }}>
            {PROFESSION_IDS.map((p) => <option key={p} value={p}>{professions[p].label.en} · {professions[p].label.th}</option>)}
          </select>
        </label>
        <div style={{ display: 'flex', gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Title</span>
            <select className="select" value={titlePrefix} onChange={(e) => setTitlePrefix(e.target.value)}>
              <option value="">—</option>
              {prof.prefixes.map((px) => <option key={px.th} value={px.th}>{px.th} ({px.en})</option>)}
            </select>
          </label>
          {prof.suffixes.length > 0 && (
            <label className="field" style={{ flex: 1 }}>
              <span className="label">Designation</span>
              <select className="select" value={titleSuffix} onChange={(e) => setTitleSuffix(e.target.value)}>
                <option value="">—</option>
                {prof.suffixes.map((sx) => <option key={sx} value={sx}>{sx}</option>)}
              </select>
            </label>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Department</span>
            <select className="select" value={deptId} onChange={(e) => { setDeptId(e.target.value); const u = org.find((d) => d.deptId === e.target.value)?.units ?? []; setUnitId(u[0]?.id ?? ''); }}>
              {org.map((d) => <option key={d.deptId} value={d.deptId}>{d.dept}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Unit</span>
            <select className="select" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
              {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </label>
        </div>
        <label className="field"><span className="label">Email</span><input className="input" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <button className="btn btn-primary btn-block" disabled={adminUpdate.isPending} onClick={save}>
          {adminUpdate.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}
